<script>
import { mapGetters } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import ChatList from '../../../components/ChatList.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import { emitter } from 'shared/helpers/mitt';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';

export default {
  components: {
    ChatList,
    ConversationBox,
    CmdBarConversationSnooze,
    SidepanelSwitch,
    ConversationSidebar,
  },
  beforeRouteLeave(to, from, next) {
    // Clear selected state if navigating away from a conversation to a route without a conversationId to prevent stale data issues
    // and resolves timing issues during navigation with conversation view and other screens
    if (this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
    next(); // Continue with navigation
  },
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
    issueId: {
      type: [String, Number],
      default: 0,
    },
    fixedAssigneeType: {
      type: String,
      default: '',
    },
    fixedStatus: {
      type: String,
      default: '',
    },
    pageTitle: {
      type: String,
      default: '',
    },
    hideFilterControls: {
      type: Boolean,
      default: false,
    },
    hideCollapseToggle: {
      type: Boolean,
      default: false,
    },
    disableSidebar: {
      type: Boolean,
      default: false,
    },
    isQAMonitor: {
      type: Boolean,
      default: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
    hideMessageViewWhenEmpty: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();

    return {
      uiSettings,
      updateUISettings,
      accountId,
    };
  },
  data() {
    return {
      showSearchModal: false,
      qaFilterCommand: null,
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
    }),
    showConversationList() {
      return this.isOnExpandedLayout ? !this.conversationId : true;
    },
    showMessageView() {
      if (this.conversationId) {
        return true;
      }

      if (this.hideMessageViewWhenEmpty) {
        return false;
      }

      return !this.isOnExpandedLayout;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },

    shouldShowSidebar() {
      if (this.disableSidebar) {
        return false;
      }

      if (!this.currentChat.id) {
        return false;
      }

      const {
        is_contact_sidebar_open: isContactSidebarOpen,
        is_ai_assistant_open: isAiAssistantOpen,
      } = this.uiSettings;

      return isContactSidebarOpen || isAiAssistantOpen;
    },
  },
  watch: {
    conversationId() {
      this.fetchConversationIfUnavailable();
      this.setActiveChat();
    },
  },

  created() {
    // Clear selected state early if no conversation is selected
    // This prevents child components from accessing stale data
    // and resolves timing issues during navigation
    // with conversation view and other screens
    if (!this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
  },

  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('portals/index');
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });
  },

  methods: {
    onConversationLoad() {
      this.fetchConversationIfUnavailable();
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.setActiveChat();
    },
    toggleConversationLayout() {
      const { LAYOUT_TYPES } = wootConstants;
      const {
        conversation_display_type:
          conversationDisplayType = LAYOUT_TYPES.CONDENSED,
      } = this.uiSettings;
      const newViewType =
        conversationDisplayType === LAYOUT_TYPES.CONDENSED
          ? LAYOUT_TYPES.EXPANDED
          : LAYOUT_TYPES.CONDENSED;
      this.updateUISettings({
        conversation_display_type: newViewType,
        previously_used_conversation_display_type: newViewType,
      });
    },
    fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        return;
      }
      const chat = this.findConversation();
      if (!chat) {
        this.$store.dispatch('getConversation', this.conversationId);
      }
    },
    findConversation() {
      const conversationId = parseInt(this.conversationId, 10);
      const [chat] = this.chatList.filter(c => c.id === conversationId);
      return chat;
    },
    setActiveChat() {
      if (this.conversationId) {
        const selectedConversation = this.findConversation();
        // If conversation doesn't exist or selected conversation is same as the active
        // conversation, don't set active conversation.
        if (
          !selectedConversation ||
          selectedConversation.id === this.currentChat.id
        ) {
          return;
        }
        const { messageId } = this.$route.query;
        this.$store
          .dispatch('setActiveChat', {
            data: selectedConversation,
            after: messageId,
          })
          .then(() => {
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
          });
      } else {
        this.$store.dispatch('clearSelectedState');
      }
    },
    onSearch() {
      this.showSearchModal = true;
    },
    closeSearch() {
      this.showSearchModal = false;
    },
    onQaFiltersApply(payload) {
      this.qaFilterCommand = {
        type: 'apply',
        payload,
        nonce: Date.now(),
      };
    },
    onQaFiltersClear() {
      this.qaFilterCommand = {
        type: 'clear',
        nonce: Date.now(),
      };
    },
  },
};
</script>

<template>
  <section
    class="conversation-page-theme flex w-full h-full min-w-0"
    style="background-color: var(--wlab-bg-sidebar)"
  >
    <ChatList
      :show-conversation-list="showConversationList"
      :conversation-inbox="inboxId"
      :label="label"
      :team-id="teamId"
      :conversation-type="conversationType"
      :folders-id="foldersId"
      :issue-id="issueId"
      :qa-filter-command="qaFilterCommand"
      :is-on-expanded-layout="isOnExpandedLayout"
      :fixed-assignee-type="fixedAssigneeType"
      :fixed-status="fixedStatus"
      :page-title="pageTitle"
      :hide-filter-controls="hideFilterControls"
      :hide-collapse-toggle="hideCollapseToggle"
      :is-qa-monitor="isQAMonitor"
      @conversation-load="onConversationLoad"
    />
    <ConversationBox
      v-if="showMessageView"
      :inbox-id="inboxId"
      :is-qa-monitor="isQAMonitor"
      :is-inbox-view="isInboxView"
      :is-on-expanded-layout="isOnExpandedLayout"
      @qa-filters-apply="onQaFiltersApply"
      @qa-filters-clear="onQaFiltersClear"
    >
      <SidepanelSwitch v-if="currentChat.id && !disableSidebar" />
    </ConversationBox>
    <Transition name="conversation-sidebar-mobile">
      <ConversationSidebar
        v-if="shouldShowSidebar"
        :current-chat="currentChat"
      />
    </Transition>
    <CmdBarConversationSnooze />
  </section>
</template>

<style>
@media (max-width: 768px) {
  .conversation-sidebar-mobile-enter-active,
  .conversation-sidebar-mobile-leave-active {
    transition:
      opacity 220ms cubic-bezier(0.22, 1, 0.36, 1),
      transform 320ms cubic-bezier(0.22, 1, 0.36, 1),
      filter 320ms cubic-bezier(0.22, 1, 0.36, 1);
    will-change: opacity, transform, filter;
  }

  .conversation-sidebar-mobile-enter-from,
  .conversation-sidebar-mobile-leave-to {
    opacity: 0;
    transform: translate3d(18px, 0, 0) scale(0.965);
    filter: blur(8px);
  }

  .conversation-sidebar-mobile-enter-to,
  .conversation-sidebar-mobile-leave-from {
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
    filter: blur(0);
  }

  .conversation-sidebar-mobile-enter-from .conversation-sidebar-panel,
  .conversation-sidebar-mobile-leave-to .conversation-sidebar-panel {
    border-radius: 28px !important;
    box-shadow: 0 22px 48px rgba(0, 0, 0, 0.22);
  }

  .conversation-sidebar-mobile-enter-to .conversation-sidebar-panel,
  .conversation-sidebar-mobile-leave-from .conversation-sidebar-panel {
    border-radius: 0 !important;
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.04),
      0 0 0 1px rgba(17, 24, 39, 0.04);
  }
}
</style>
